/**
 * ChatMessage Component
 *
 * Displays individual chat messages with role-based styling.
 * User messages appear on the right with purple background,
 * AI messages appear on the left with slate background.
 */

import { motion } from 'motion/react'
import { User, Bot, AlertCircle, Loader2, Code2, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AgentMessage } from '@/lib/agent-chat'

/**
 * Checks if content appears to be HTML document (from AI template response)
 */
function isHtmlContent(content: string): boolean {
  if (!content || content.length < 50) return false
  // Check for HTML document indicators
  return (
    content.includes('<!DOCTYPE') ||
    content.includes('<html') ||
    content.includes('<body') ||
    (content.includes('<table') && content.includes('</table>'))
  )
}

/**
 * Renders a compact placeholder for HTML content instead of raw markup
 */
function HtmlPlaceholder({ isStreaming = false }: { isStreaming?: boolean }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className={cn(
        "flex items-center justify-center w-8 h-8 rounded-lg border",
        isStreaming
          ? "bg-blue-100 border-blue-200"
          : "bg-emerald-100 border-emerald-200"
      )}>
        {isStreaming ? (
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
        ) : (
          <Code2 className="w-4 h-4 text-emerald-600" />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-1.5">
          {isStreaming ? (
            <>
              <span className="text-sm font-medium text-slate-700">Generating changes...</span>
            </>
          ) : (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-sm font-medium text-slate-700">Template updated</span>
            </>
          )}
        </div>
        <p className="text-xs text-slate-500">
          {isStreaming ? "Applying to editor" : "Changes applied to editor"}
        </p>
      </div>
    </div>
  )
}

interface ChatMessageProps {
  message: AgentMessage
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isStreaming = message.isStreaming
  const hasError = Boolean(message.error)

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "flex gap-2 mb-3",
        isUser ? "justify-end" : "justify-start"
      )}
    >
      {/* AI Avatar (left side) */}
      {!isUser && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center mt-1">
          <Bot className="w-4 h-4 text-slate-600" />
        </div>
      )}

      {/* Message Bubble */}
      <div className={cn(
        "max-w-[80%] rounded-lg px-3 py-2 relative",
        isUser
          ? "bg-eventus-purple text-white"
          : hasError
          ? "bg-red-50 border border-red-200 text-red-900"
          : "bg-slate-100 text-slate-900",
        isStreaming && !isUser && "animate-pulse"
      )}>
        {/* Error Icon */}
        {hasError && (
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-medium text-red-700">Error</span>
          </div>
        )}

        {/* Message Content */}
        <div className={cn(
          "text-sm whitespace-pre-wrap break-words",
          isUser && "text-white"
        )}>
          {/* Show placeholder for HTML content from AI (streaming or complete) */}
          {!isUser && !hasError && message.content && isHtmlContent(message.content) ? (
            <HtmlPlaceholder isStreaming={isStreaming} />
          ) : message.content || (isStreaming && !hasError && (
            // Bouncing dots while streaming with empty content
            <div className="flex items-center gap-1">
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, delay: 0 }}
                className="w-1.5 h-1.5 rounded-full bg-slate-400"
              />
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, delay: 0.1 }}
                className="w-1.5 h-1.5 rounded-full bg-slate-400"
              />
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, delay: 0.2 }}
                className="w-1.5 h-1.5 rounded-full bg-slate-400"
              />
            </div>
          ))}
        </div>

        {/* Streaming Indicator */}
        {isStreaming && message.content && !hasError && (
          <div className="absolute -bottom-1 -right-1">
            <Loader2 className="w-3 h-3 text-eventus-purple animate-spin" />
          </div>
        )}

        {/* Timestamp */}
        <div className={cn(
          "text-xs mt-1 opacity-70",
          isUser ? "text-white" : "text-slate-500"
        )}>
          {message.timestamp.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
          })}
        </div>
      </div>

      {/* User Avatar (right side) */}
      {isUser && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-eventus-purple/20 flex items-center justify-center mt-1">
          <User className="w-4 h-4 text-eventus-purple" />
        </div>
      )}
    </motion.div>
  )
}
